'use client';

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import { TrackedButtonProps } from '@/components';

type ButtonPropsOmitted = Omit<
  TrackedButtonProps,
  'fulWidth' | 'size' | 'styles' | 'className' | 'classNames'
>;

interface FooterContextType {
  buttonPrimaryProps: ButtonPropsOmitted;
  buttonSecondaryProps: ButtonPropsOmitted;
  setButtonPrimaryProps: (props: ButtonPropsOmitted) => void;
  setButtonSecondaryProps: (props: ButtonPropsOmitted) => void;
  clearButtonPrimaryProps: () => void;
  clearButtonSecondaryProps: () => void;
}

const FooterContext = createContext<FooterContextType | undefined>(undefined);

export const FooterProvider = ({ children }: { children: ReactNode }) => {
  const [buttonPrimaryProps, setButtonPrimaryProps] =
    useState<ButtonPropsOmitted>({});

  const [buttonSecondaryProps, setButtonSecondaryProps] =
    useState<ButtonPropsOmitted>({});

  const clearButtonPrimaryProps = useCallback(() => {
    setButtonPrimaryProps({});
  }, []);

  const clearButtonSecondaryProps = useCallback(() => {
    setButtonSecondaryProps({});
  }, []);

  return (
    <FooterContext.Provider
      value={{
        buttonPrimaryProps,
        buttonSecondaryProps,
        setButtonPrimaryProps,
        setButtonSecondaryProps,
        clearButtonPrimaryProps,
        clearButtonSecondaryProps,
      }}
    >
      {children}
    </FooterContext.Provider>
  );
};

export const useFooter = (): FooterContextType => {
  const context = useContext(FooterContext);
  if (context === undefined) {
    throw new Error('useFooter must be used within a FooterProvider');
  }
  return context;
};
